import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, Loader2, AlertCircle, GripVertical } from 'lucide-react';
import { patternService } from '../services/patternService';
import { usePatternStudio } from '../context/PatternStudioContext';

const MAX_SIZE_MB = 10;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

function UploadTile({ photo, onRemove }) {
  return (
    <div className="relative group rounded-xl overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '1' }}>
      <img src={photo.url} alt={photo.caption || ''} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all" />
      <button
        onClick={() => onRemove(photo.id)}
        className="absolute top-1.5 right-1.5 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50"
      >
        <X size={12} className="text-gray-700" />
      </button>
    </div>
  );
}

function PendingTile({ file }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-50 flex items-center justify-center" style={{ aspectRatio: '1' }}>
      <div className="text-center p-2">
        <Loader2 size={20} className="animate-spin mx-auto mb-1" style={{ color: '#6B5744' }} />
        <p className="text-xs text-gray-500 truncate max-w-full px-1">{file.name}</p>
      </div>
    </div>
  );
}

export default function PhotoDropzone({ category = 'cover', label, hint, maxPhotos = 10 }) {
  const { state, dispatch } = usePatternStudio();
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState('');

  const photos = state.pattern.photos.filter(p => p.category === category);

  const processFiles = useCallback(async (files) => {
    setError('');
    const valid = [];
    for (const file of files) {
      if (!ACCEPTED.includes(file.type)) { setError('Only JPEG, PNG or WebP images.'); continue; }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) { setError(`Max ${MAX_SIZE_MB}MB per image.`); continue; }
      valid.push(file);
    }
    if (!valid.length) return;
    if (photos.length + valid.length > maxPhotos) {
      setError(`Max ${maxPhotos} photos per category.`);
      return;
    }

    setPending(valid);

    for (const file of valid) {
      try {
        let url;
        if (state.patternId) {
          url = await patternService.uploadPhoto(state.patternId, file, category);
        } else {
          // No pattern ID yet — use local object URL for live preview; will persist on next save
          url = URL.createObjectURL(file);
        }
        dispatch({ type: 'ADD_PHOTO', url, category });
      } catch (e) {
        setError('Upload failed: ' + e.message);
      }
    }
    setPending([]);
  }, [state.patternId, photos.length, category, dispatch, maxPhotos]);

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    processFiles(Array.from(e.dataTransfer.files));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">{label}</p>
        {photos.length > 0 && (
          <span className="text-xs text-gray-400">{photos.length}/{maxPhotos}</span>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mb-3">{hint}</p>}

      {error && (
        <div className="flex items-center gap-2 mb-3 p-2.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
          <AlertCircle size={13} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X size={12} /></button>
        </div>
      )}

      {/* Grid */}
      {(photos.length > 0 || pending.length > 0) && (
        <div className="grid grid-cols-3 gap-3 mb-3">
          {photos.map(p => (
            <UploadTile
              key={p.id}
              photo={p}
              onRemove={id => dispatch({ type: 'REMOVE_PHOTO', id })}
            />
          ))}
          {pending.map(f => <PendingTile key={f.name} file={f} />)}
        </div>
      )}

      {/* Drop zone */}
      {photos.length < maxPhotos && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${
            dragging ? 'border-stone-500 bg-stone-100' : 'border-stone-200 bg-stone-50 hover:border-stone-400 hover:bg-stone-100'
          }`}
        >
          <Upload size={20} className="mb-2" style={{ color: '#6B5744' }} />
          <p className="text-sm text-gray-600 font-medium">Drop photos here or click to browse</p>
          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP · Max {MAX_SIZE_MB}MB</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(',')}
            multiple
            className="hidden"
            onChange={e => processFiles(Array.from(e.target.files))}
          />
        </div>
      )}
    </div>
  );
}
